import React, { useCallback, useEffect, useState } from "react";
import type { Timestamp } from "@bufbuild/protobuf";
import { OrganizationAccessLevel } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/organization_access_pb";
import { Spinner } from "@kui-react/spinner";
import { formatDistanceToNow } from "date-fns";

import { Button } from "components/Graphs/Button";
import {
  TeamTable,
  TeamTableBody,
  TeamTableCell,
  TeamTableHead,
  TeamTableHeaderCell,
} from "./TeamTable";
import { toast } from "utils/toast";
import { invitationClient, type Invitation } from "server/devplane/Invitations";
import { BREV_SUPPORT_EMAIL } from "constants/index";

interface TeamInvitationsProps {
  organizationId: string;
}

function timestampToDate(timestamp?: Timestamp): Date | null {
  if (!timestamp) return null;
  if (typeof timestamp.toDate === "function") {
    return timestamp.toDate();
  }
  return null;
}

function formatRelativeTimestamp(timestamp?: Timestamp): string {
  const date = timestampToDate(timestamp);
  if (!date || isNaN(date.getTime())) return "Never";
  return formatDistanceToNow(date, { addSuffix: true });
}

function formatTimestampTitle(timestamp?: Timestamp): string | undefined {
  const date = timestampToDate(timestamp);
  if (!date || isNaN(date.getTime())) return undefined;
  return date.toLocaleString(undefined, {
    dateStyle: "full",
    timeStyle: "long",
  });
}

function isInvitationExpired(invitation: Invitation): boolean {
  const expiresAt = timestampToDate(invitation.expiresAt);
  return Boolean(expiresAt && expiresAt.getTime() <= Date.now());
}

function getAccessLevelLabel(accessLevel: OrganizationAccessLevel): string {
  switch (accessLevel) {
    case OrganizationAccessLevel.ADMIN:
      return "Admin";
    case OrganizationAccessLevel.MEMBER:
      return "Member";
    case OrganizationAccessLevel.OWNER:
      return "Owner";
    case OrganizationAccessLevel.MAINTAINER:
      return "Maintainer";
    default:
      return "Unknown";
  }
}

const TeamInvitations: React.FC<TeamInvitationsProps> = ({
  organizationId,
}) => {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [deletingInvitationId, setDeletingInvitationId] = useState("");

  const loadInvitations = useCallback(async () => {
    if (!organizationId) {
      setInvitations([]);
      return;
    }

    setIsLoading(true);
    setError("");
    try {
      const res = await invitationClient.listInvitations({
        organizationId,
        pending: true,
        expired: true,
      });
      setInvitations(res.invitations);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "";
      setError(message || "Failed to load invitations");
      setInvitations([]);
    } finally {
      setIsLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    loadInvitations();
  }, [loadInvitations]);

  const deleteInvitation = async (invitation: Invitation) => {
    setDeletingInvitationId(invitation.invitationId);
    try {
      await invitationClient.revokeInvitation({
        organizationId,
        invitationId: invitation.invitationId,
      });
      toast.success("Invitation deleted", {
        description: `${invitation.invitationEmail} was removed from this list.`,
      });
      await loadInvitations();
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "";
      toast.error("Unable to delete invitation", {
        description:
          message ||
          `An unexpected error occurred. Contact support at ${BREV_SUPPORT_EMAIL}.`,
      });
    } finally {
      setDeletingInvitationId("");
    }
  };

  return (
    <div className="flex w-full flex-col dark:text-secondary">
      <div>
        {isLoading && (
          <div className="mt-5">
            <Spinner size="medium" />
          </div>
        )}

        {!isLoading && error && (
          <div
            role="alert"
            className="rounded-md border border-red-900 bg-red-950/40 p-3 text-sm text-red-300"
          >
            {error}
          </div>
        )}

        {!isLoading && !error && invitations.length === 0 && (
          <div className="rounded-md border border-zinc-800 p-4 text-sm text-secondary">
            No invitations found.
          </div>
        )}

        {!isLoading && !error && invitations.length > 0 && (
          <TeamTable>
            <TeamTableHead>
              <tr>
                <TeamTableHeaderCell>Email</TeamTableHeaderCell>
                <TeamTableHeaderCell>Role</TeamTableHeaderCell>
                <TeamTableHeaderCell>Invited</TeamTableHeaderCell>
                <TeamTableHeaderCell>Expires</TeamTableHeaderCell>
                <TeamTableHeaderCell>
                  <span className="sr-only">Actions</span>
                </TeamTableHeaderCell>
              </tr>
            </TeamTableHead>
            <TeamTableBody>
              {invitations.map((invitation, index) => {
                const expired = isInvitationExpired(invitation);
                const isLastRow = index === invitations.length - 1;

                return (
                  <tr key={invitation.invitationId}>
                    <TeamTableCell isLastRow={isLastRow}>
                      {invitation.invitationEmail}
                    </TeamTableCell>
                    <TeamTableCell isLastRow={isLastRow}>
                      {getAccessLevelLabel(invitation.accessLevel)}
                    </TeamTableCell>
                    <TeamTableCell isLastRow={isLastRow}>
                      <span title={formatTimestampTitle(invitation.invitedAt)}>
                        {formatRelativeTimestamp(invitation.invitedAt)}
                      </span>
                    </TeamTableCell>
                    <TeamTableCell isLastRow={isLastRow}>
                      <span
                        className={
                          expired
                            ? "italic text-gray-500 dark:text-gray-400"
                            : undefined
                        }
                        title={formatTimestampTitle(invitation.expiresAt)}
                      >
                        {expired ? "Expired" : "Expires"}{" "}
                        {formatRelativeTimestamp(invitation.expiresAt)}
                      </span>
                    </TeamTableCell>
                    <TeamTableCell isLastRow={isLastRow} className="text-right">
                      <Button
                        variant="secondary"
                        className="border-red-600 bg-transparent text-red-600 hover:bg-red-50 hover:text-red-700 disabled:border-red-300 disabled:bg-transparent disabled:text-red-300 dark:border-red-700 dark:bg-transparent dark:text-red-400 dark:hover:bg-red-950/30 dark:hover:text-red-300 dark:disabled:border-red-950 dark:disabled:text-red-500"
                        disabled={!organizationId}
                        isLoading={
                          deletingInvitationId === invitation.invitationId
                        }
                        loadingText="Deleting"
                        onClick={() => deleteInvitation(invitation)}
                      >
                        Delete
                      </Button>
                    </TeamTableCell>
                  </tr>
                );
              })}
            </TeamTableBody>
          </TeamTable>
        )}
      </div>
    </div>
  );
};

export default TeamInvitations;
